import React, { useState, useEffect, useCallback } from "react";
import {
  Heading,
  Table,
  Button,
  TextInput,
  toaster,
  Text,
  Switch,
  Dialog,
} from "evergreen-ui";
import { connect } from "react-redux";
import { setLoading, unsetLoading } from "../../../Redux/reducers/loading";
import axios from "../../../Utils/axios";

const DAYS_OF_WEEK = [
  { id: 1, label: "Domingo" },
  { id: 2, label: "Segunda" },
  { id: 3, label: "Terça" },
  { id: 4, label: "Quarta" },
  { id: 5, label: "Quinta" },
  { id: 6, label: "Sexta" },
  { id: 7, label: "Sábado" },
];

const DEFAULT_MODAL_DAYS = [2, 3, 4, 5, 6];

const normalizeDays = (days = []) =>
  days.some((day) => day === 0)
    ? days.map((day) => day + 1)
    : days;

const dayLabelById = DAYS_OF_WEEK.reduce((acc, day) => {
  acc[day.id] = day.label;
  return acc;
}, {});

const TimeClock = ({ dispatch }) => {
  const [active, setActive] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalHour, setModalHour] = useState("");
  const [modalDays, setModalDays] = useState(DEFAULT_MODAL_DAYS);

  const save = useCallback(async () => {
    try {
      await axios.post(`/company/timeclock`, {
        active,
        alerts: active ? alerts : [],
      });
    } catch (error) {
      if (error.response) {
        toaster.danger(error.response.data.error.message);
      } else {
        toaster.danger("Falha na comunicação com o servidor");
      }
    }
  }, [active, alerts]);

  const getTimeClock = useCallback(async () => {
    setIsLoaded(false);
    dispatch(setLoading());
    try {
      const { data } = await axios.get(`/company/timeclock`);
      setActive(data.active);
      setAlerts(
        (data.alerts || []).map((alert) => ({
          ...alert,
          days: normalizeDays(alert.days),
        })),
      );
      setIsLoaded(true);
    } catch (error) {
      toaster.danger("Falha ao buscar dados");
    } finally {
      dispatch(unsetLoading());
    }
  }, [dispatch]);

  useEffect(() => {
    getTimeClock();
  }, [getTimeClock]);

  useEffect(() => {
    if (isLoaded) {
      save();
    }
  }, [alerts, active, isLoaded, save]);

  const handleAddAlert = () => {
    if (!modalHour) {
      return toaster.danger("Hora inválida!");
    }

    if (modalDays.length === 0) {
      return toaster.danger("Selecione pelo menos um dia!");
    }

    if (
      alerts.some(
        (alert) =>
          alert.hour === modalHour &&
          JSON.stringify(alert.days) === JSON.stringify(modalDays),
      )
    ) {
      return toaster.danger("Alerta para estes dias e hora já existe!");
    }

    const newAlert = {
      id: Date.now(),
      hour: modalHour,
      days: [...modalDays].sort((a, b) => a - b),
    };

    setAlerts(
      [...alerts, newAlert].sort((a, b) => a.hour.localeCompare(b.hour)),
    );
    setModalHour("");
    setModalDays(DEFAULT_MODAL_DAYS);
    setShowModal(false);
    toaster.success("Alerta adicionado com sucesso!");
  };

  const handleRemoveAlert = (id) => {
    setAlerts(alerts.filter((alert) => alert.id !== id));
    toaster.success("Alerta removido!");
  };

  const toggleDay = (day) => {
    if (modalDays.includes(day)) {
      setModalDays(modalDays.filter((d) => d !== day));
    } else {
      setModalDays([...modalDays, day]);
    }
  };

  function handleActive(activate) {
    if (!activate) {
      setAlerts([]);
    }
    setActive(activate);
  }

  const renderActive = () => (
    <>
      <Heading size={600}>Configuração de Ponto</Heading>
      <div>
        <Text>Trabalha com registro ponto</Text>
        <Switch
          name="active"
          height={24}
          checked={active}
          onChange={() => handleActive(!active)}
          marginBottom={16}
        />
      </div>
    </>
  );

  if (!active) {
    return renderActive();
  }

  return (
    <div>
      {renderActive()}
      <div style={{ marginTop: 24, marginBottom: 16 }}>
        <Button appearance="primary" onClick={() => setShowModal(true)}>
          + Adicionar Alerta
        </Button>
      </div>

      <Dialog
        isShown={showModal}
        title="Adicionar Novo Alerta"
        onCloseComplete={() => {
          setShowModal(false);
          setModalHour("");
          setModalDays(DEFAULT_MODAL_DAYS);
        }}
        confirmLabel="Adicionar"
        onConfirm={handleAddAlert}
        isConfirmLoading={false}
        width={650}
      >
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", marginBottom: 8 }}>
            <strong>Hora do Alerta</strong>
          </label>
          <TextInput
            name="time"
            placeholder="Selecione a hora"
            type="time"
            value={modalHour}
            onChange={({ target }) => setModalHour(target.value)}
            width="100%"
          />
        </div>

        <div>
          <label style={{ display: "block", marginBottom: 16 }}>
            <strong>Dias da semana</strong>
          </label>
          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {DAYS_OF_WEEK.map((day) => (
              <Button
                key={day.id}
                type="button"
                onClick={() => toggleDay(day.id)}
                appearance={modalDays.includes(day.id) ? "primary" : "default"}
              >
                {day.label}
              </Button>
            ))}
          </div>
        </div>
      </Dialog>

      <div style={{ marginTop: 24 }}>
        <Heading size={600}>Alertas Configurados</Heading>
        {alerts.length === 0 ? (
          <Text color="muted" marginTop={16}>
            Nenhum alerta configurado
          </Text>
        ) : (
          <Table style={{ marginTop: 16 }}>
            <Table.Head>
              <Table.TextHeaderCell>Hora</Table.TextHeaderCell>
              <Table.TextHeaderCell>Dias da semana</Table.TextHeaderCell>
              <Table.TextHeaderCell>Ações</Table.TextHeaderCell>
            </Table.Head>
            <Table.Body>
              {alerts.map((alert) => (
                <Table.Row key={alert.id}>
                  <Table.TextCell>{alert.hour}</Table.TextCell>
                  <Table.TextCell>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                      {alert.days.map((day) => (
                        <span
                          key={day}
                          style={{
                            padding: "2px 8px",
                            backgroundColor: "#f0f0f0",
                            borderRadius: 3,
                            fontSize: 11,
                            border: "1px solid #d9d9d9",
                          }}
                        >
                          {(dayLabelById[day] || "-").substring(0, 3)}
                        </span>
                      ))}
                    </div>
                  </Table.TextCell>
                  <Table.TextCell>
                    <Button
                      iconBefore="edit"
                      marginRight={20}
                      onClick={() => handleRemoveAlert(alert.id)}
                    >
                      Remover
                    </Button>
                  </Table.TextCell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </div>
    </div>
  );
};

export default connect()(TimeClock);
